import { LIST } from '@adalo/constants'

import {
  makePush,
  calculateHeightFromChildren,
} from 'utils/variableHeight/runtime'
import { overlapsRangeWithDifferentY, getXRangeWithY } from './range'

import { RunnerPartialObject, Node } from './types'

import { deepMap, traverse } from './treeTraversal'

export const getNode = ({
  id,
  attributes: { name, width, x, height, adjustedY, deviceVisibility },
  newHeight,
  newY,
  type,
}: RunnerPartialObject) => {
  let heightOffset = 0
  if (newHeight && newHeight > height) {
    heightOffset = newHeight - height
  }

  let yOffset = 0
  if (newY !== undefined) {
    yOffset = newY - adjustedY
  }

  return {
    name,
    range: getXRangeWithY(x, width, adjustedY),
    objectId: id,
    originalY: adjustedY,
    originalHeight: height,
    yOffset,
    type,
    heightOffset,
    pushedBy: [],
    pushes: [],
    children: [],
    deviceVisibility,
  }
}

export const sortByY = (obj1: Node, obj2: Node) => obj1.range.y - obj2.range.y

export const searchConnected = (
  search: Node,
  toSearch: Node[],
  visited: Set<string>
): void => {
  for (const index in toSearch) {
    const obj = toSearch[index]

    if (
      !overlapsRangeWithDifferentY(obj.range, search.range) ||
      alreadyPushes(search, obj)
    ) {
      continue
    }

    if (!visited.has(obj.objectId)) {
      const i = parseInt(index)
      searchConnected(obj, toSearch.slice(i + 1), visited)
      visited.add(obj.objectId)
    }

    search.pushes.push(obj)
    obj.pushedBy.push(search)
  }
}

export const alreadyPushes = (head: Node, toFind: Node): boolean => {
  if (!head.pushes) {
    return false
  }

  for (const obj of head.pushes) {
    if (obj.objectId === toFind.objectId) {
      return true
    }

    if (alreadyPushes(obj, toFind)) {
      return true
    }
  }

  return false
}

export const generatePushTree = (object: Node): void => {
  if (object.type === LIST) {
    object.children = []
    return
  }

  if (!object?.children?.length) {
    return
  }

  addChildrenToPushTree(object)
}

export const addChildrenToPushTree = (object: Node): void => {
  object.children.sort(sortByY)
  const visited = new Set<string>()

  for (const index in object.children) {
    const obj = object.children[index]

    if (!visited.has(obj.objectId)) {
      const i = parseInt(index)
      searchConnected(obj, object.children.slice(i + 1), visited)
    }
  }
}

export const setParents = (node: Node) => {
  node.children.forEach(child => {
    child.parent = node
  })
}

export const generatePushTreeForAll = (body: RunnerPartialObject[]): Node[] => {
  const nodes = deepMap(body, getNode)
  traverse(nodes, setParents)
  traverse(nodes, generatePushTree)
  checkHeightChanges(nodes)
  return nodes
}

export const addToMap = (node: Node, map: Record<string, Node>): void => {
  map[node.objectId] = node
}

export const getNodesMap = (body: Node[]): Record<string, Node> => {
  const map: Record<string, Node> = {}
  traverse(body, obj => addToMap(obj, map))
  return map
}

const checkHeightChanges = (nodes: Node[]) => {
  traverse(nodes, node => {
    if (node.heightOffset) {
      makePush(node)
      if (node.parent) {
        calculateHeightFromChildren(node.parent)
      }
    }
  })
}
